import { InternalChargeCardAccount } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import { useToggleSafePay } from 'features/believe/learningCenter/hooks/useToggleSafePay';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import { SafePayOff } from './SafePayModes/SafePayOff';
import { SafePayOn } from './SafePayModes/SafePayOn';

const SNACKBAR_DURATION = 5000;

const HowBuildCreditScreen = () => {
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'learningCenter',
  });
  const [buttonClicked, setButtonClicked] = useState(false);
  const { accountId } = useLocalSearchParams<{ accountId: string }>();
  const { refetch, accountDetails, loading } = useAccountDetails(accountId);

  const {
    toggleSafePay,
    loading: fetching,
    errorModel,
    response,
  } = useToggleSafePay();
  const showLoader = loading || fetching;

  const handleOnSafePayButtonAnalytics = usePressableAnalytics({
    analyticsId: 'safePayOn',
  });

  const onButtonPress = () => {
    handleOnSafePayButtonAnalytics();
    toggleSafePay(true);
    setButtonClicked(true);
  };

  useEffect(() => {
    if (response) {
      refetch?.();
    }
  }, [refetch, response]);

  const isEnabled = (accountDetails as InternalChargeCardAccount)
    ?.isSafePayEnabled;

  return (
    <View style={styles.outerContainer}>
      <Stack.Screen
        options={{
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={showLoader}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
      <ActionScreen
        visual="CARD_A_ANIMATED"
        titleText={translatedText(
          'howToBuildCredit.buildCreditTitleString.screenLabel',
        )}
        titleTextStyle="extra-large"
        primaryButton={
          !isEnabled
            ? {
                label: translatedText('howToBuildCredit.buttonString.text'),
                onPress: onButtonPress,
              }
            : undefined
        }
        bodyContent={
          <View style={styles.safePayContainer}>
            {!isEnabled ? (
              <View style={styles.safePayOnContainer}>
                <SafePayOff />
              </View>
            ) : (
              <View style={styles.safePayOffContainer}>
                <SafePayOn />
              </View>
            )}
          </View>
        }
      />

      {isEnabled && buttonClicked ? (
        <Snackbar
          text={translatedText('howToBuildCredit.snackBarString.text')}
          disableAutoHide={false}
          buttonOnPress="CLOSE"
          duration={SNACKBAR_DURATION}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  safePayContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  safePayOffContainer: {
    backgroundColor: theme.color.background.primary,
    flex: isWeb ? undefined : 1,
    justifyContent: 'center',
  },
  safePayOnContainer: {
    backgroundColor: theme.color.background.primary,
    flex: isWeb ? undefined : 1,

    justifyContent: 'center',
  },
});

export default HowBuildCreditScreen;
